"use client";

import Button from "@/components/Button";
import ErrorField from "@/components/ErrorField";
import FacebookIcon from "@/components/icons/FacebookIcon";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import XTwitterIcon from "@/components/icons/XTwitterIcon";
import { useModal } from "@/components/Modal";
import { useUserContext } from "@/context/UserContext";
import { handleApiErr, useAPI } from "@/lib/api";
import toast from "react-hot-toast";

import InputHiddenCaptchaModal from "@/components/InputHiddenCaptchaModal";
import { cn } from "@/lib/utils";
import { linkValidation, sidLinkValidation } from "@/vars/link-validation";
import shortUrl from "@/vars/shortUrl";
import { Field, Form, Formik } from "formik";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  DownloadIcon,
  LinkIcon,
  Loader2,
  NewspaperIcon,
  QrCodeIcon,
  SearchIcon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Fragment, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCopyToClipboard } from "react-use";
import * as Yup from "yup";

const QRRender = dynamic(() => import("@/components/QRRender"), {
  ssr: false,
});

export default function PublicCreation({
  initial,
  single,
}: {
  initial?: string;
  single?: boolean;
}) {
  const [section, setsection] = useState(initial || "shortener");

  const { status } = useUserContext();
  const { t } = useTranslation();

  const sections = {
    shortener: {
      icon: <LinkIcon className="mr-2 -ml-1 size-4" />,
      title: t("shortener"),
    },
    qr: {
      icon: <QrCodeIcon className="mr-2 -ml-1 size-4" />,
      title: t("qr_code"),
    },
    microsite: {
      icon: <NewspaperIcon className="mr-2 -ml-1 size-4" />,
      title: t("microsite"),
    },
  };

  if (status === "unauthorized") {
    return (
      <div className="mx-auto mt-6 w-full max-w-3xl">
        {!single && (
          <div className="flex justify-center items-center gap-2 mb-4">
            {Object.entries(sections).map(([key, { icon, title }]) => (
              <button
                key={key}
                className={cn(
                  "transition-all",
                  "flex items-center border rounded-full",
                  "px-3 py-2 lg:px-4 lg:py-2",
                  "text-xs lg:text-base",
                  section === key
                    ? "bg-primary border-primary text-white"
                    : "bg-background hover:bg-background/50"
                )}
                onClick={() => setsection(key)}
              >
                {icon}
                {title}
              </button>
            ))}
          </div>
        )}

        <div className="relative">
          {section === "shortener" && <PublicShorten />}
          {section === "qr" && <PublicQR />}
          {section === "microsite" && <PublicMicrosite />}
        </div>
      </div>
    );
  }

  if (status === "authenticated") {
    return (
      <>
        <Link
          href="/dashboard?utm_source=landing"
          className="mx-auto mt-5 lg:mt-10"
        >
          <button className="flex items-center bg-primary/5 px-6 py-2 border border-primary rounded-full font-montserrat font-semibold text-primary text-lg lg:text-xl">
            <Trans i18nKey="goto_dashboard">Go to Dashboard</Trans>{" "}
            <ArrowRightIcon className="-mr-2 ml-2 size-5" />
          </button>
        </Link>
      </>
    );
  }

  return <Fragment />;
}

function PublicShorten() {
  const api = useAPI();
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [success, setSuccess] = useState(false);
  const [, copyText] = useCopyToClipboard();
  const [canCopy, setCanCopy] = useState(true);
  const captchaModal = useModal();
  const { authModal } = useUserContext();

  const onSubmit = async (values, { setValues, setErrors }) => {
    if (values.captcha === "") {
      captchaModal.setOpen(true);
      return;
    }

    try {
      if (!/^(http|https):\/\//.test(values.long_url)) {
        values.long_url = "https://" + values.long_url;
      }

      const data = await api.post("/shorten", {
        long_url: values.long_url,
        captcha: values.captcha,
      });

      setData(data.data);
      setSuccess(true);
    } catch (e) {
      setValues({ ...values, captcha: "" });
      handleApiErr(e, {
        setErrors,
        t,
        toast,
        onError: (msg) => {
          if (msg === "err_cooldown") {
            authModal.open({
              message: t("login_register_to_continue_shorten"),
            });
            return true;
          }
          return false;
        },
      });
    }
  };

  const onCopy = () => {
    if (!canCopy) return;
    setCanCopy(false);
    copyText(shortUrl + "/" + data.short);
    toast.success("Copied to clipboard");
    setTimeout(() => {
      setCanCopy(true);
    }, 2000);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        long_url: Yup.string()
          .test("contain-sid", t("cannot_short_sid"), (value) => {
            return !sidLinkValidation.test(value || "");
          })
          .matches(linkValidation, t("invalid_url_format"))
          .required(t("validation_required", { field: t("long_url") })),
      }),
    [t]
  );

  const createPopup = (url, name) => {
    const winHeight = 400;
    const winWidth = 600;
    const winTop = screen.height / 2 - winHeight / 2;
    const winLeft = screen.width / 2 - winWidth / 2;
    window.open(
      url,
      name,
      "top=" +
        winTop +
        ",left=" +
        winLeft +
        ",toolbar=0,status=0,width=" +
        winWidth +
        ",height=" +
        winHeight
    );
  };

  const onShare = (item) => {
    const safeUrl = encodeURIComponent(item.url);

    if (item.slug === "facebook") {
      createPopup(
        `http://www.facebook.com/sharer.php?u=${safeUrl}&t=${safeUrl}`,
        item.label
      );
    }

    if (item.slug === "twitter") {
      createPopup(
        `https://twitter.com/intent/tweet?text=${safeUrl}`,
        item.label
      );
    }

    if (item.slug === "whatsapp") {
      createPopup(`https://wa.me?text=${safeUrl}`, item.label);
    }
  };

  return (
    <div>
      <div className="relative">
        <Formik
          initialValues={{ long_url: "", captcha: "" }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form
              className={cn(
                "transition-all duration-500 relative",
                success
                  ? "pointer-events-none opacity-0 translate-y-3"
                  : "translate-y-0"
              )}
            >
              <Field
                name="long_url"
                type="text"
                className={cn(
                  "bg-white",
                  "pr-[10rem] border focus:border-primary rounded-full outline-none ring-primary focus:ring-1 w-full font-montserrat font-medium transition-all",
                  "text-base lg:text-lg",
                  "py-3 px-5 lg:px-6 lg:py-4"
                )}
                placeholder={t("shorten_placeholder")}
                disabled={isSubmitting}
              />
              <ErrorField
                className="right-0 left-0 absolute bg-red-500 mt-1 px-6 py-1 rounded-full text-white"
                name="long_url"
              />
              <div className="top-0 right-1 lg:right-2 bottom-0 absolute flex items-center">
                <Button
                  variant="primary"
                  roundFull
                  className="flex items-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ZapIcon className="size-4" />
                  )}
                  {t("shorten")}
                </Button>
              </div>
              <InputHiddenCaptchaModal
                modalProp={captchaModal}
                action="short"
              />
            </Form>
          )}
        </Formik>
        <div
          className={cn(
            "transition-all",
            "duration-500",
            "top-0  absolute flex bg-white shadow-xl px-6 py-6 rounded-3xl",
            "right-2 left-2 lg:right-0 lg:left-0",
            success
              ? "opacity-100 translate-y-0"
              : "opacity-0 pointer-events-none -translate-y-3"
          )}
        >
          <div className="flex-1">
            <h2 className="font-montserrat font-semibold text-foreground/60 text-sm lg:text-base">
              <LinkIcon className="inline-block -mt-1 size-4" />{" "}
              <Trans i18nKey="shorten_success">
                Link shortened successfully
              </Trans>
            </h2>

            <button
              className="mt-2 font-montserrat font-semibold text-xl lg:text-3xl text-left hover:underline"
              onClick={onCopy}
            >
              <span className="text-primary">s</span>.id/{data?.short}
            </button>
            <div className="mt-1 font-worksans text-foreground/50 truncate">
              {data?.long_url}
            </div>

            <div className="flex items-center gap-2 mt-4 text-foreground/50 text-sm">
              <button
                className="flex justify-center items-center hover:bg-slate-100 border rounded-full size-10 transition-all"
                onClick={() => {
                  onShare({
                    slug: "twitter",
                    url: shortUrl + "/" + data?.short,
                  });
                }}
              >
                <XTwitterIcon className="size-4" />
              </button>
              <button
                className="flex justify-center items-center hover:bg-slate-100 border rounded-full size-10 transition-all"
                onClick={() => {
                  onShare({
                    slug: "facebook",
                    url: shortUrl + "/" + data?.short,
                  });
                }}
              >
                <FacebookIcon className="size-4" />
              </button>
              <button
                className="flex justify-center items-center hover:bg-slate-100 border rounded-full size-10 transition-all"
                onClick={() => {
                  onShare({
                    slug: "whatsapp",
                    url: shortUrl + "/" + data?.short,
                  });
                }}
              >
                <WhatsAppIcon className="size-4" />
              </button>
            </div>
          </div>
          <div className="flex flex-col justify-between items-end">
            <button
              className="flex justify-center items-center border rounded-full size-10"
              onClick={() => setSuccess(false)}
            >
              <XIcon className="size-6" />
            </button>
          </div>
        </div>
      </div>
      <p className="mt-2 px-4 text-foreground/50 text-xs lg:text-sm text-center">
        <Trans i18nKey="form_agree_with_terms">
          Dengan memproses form ini anda telah setuju dengan semua{" "}
          <a
            href="/id/page/terms-of-service"
            target="_blank"
            className="text-red-500 underline"
          >
            ketentuan layanan
          </a>{" "}
          kami
        </Trans>
      </p>
    </div>
  );
}

function PublicQR() {
  const api = useAPI();
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [success, setSuccess] = useState(false);
  const [, copyText] = useCopyToClipboard();
  const [canCopy, setCanCopy] = useState(true);
  const captchaModal = useModal();
  const { authModal } = useUserContext();

  const qrRef = useRef<any>(undefined);

  const onSubmit = async (values, { setValues, setErrors }) => {
    if (values.captcha === "") {
      captchaModal.setOpen(true);
      return;
    }

    try {
      if (!/^(http|https):\/\//.test(values.long_url)) {
        values.long_url = "https://" + values.long_url;
      }

      const data = await api.post("/shorten", {
        long_url: values.long_url,
        captcha: values.captcha,
      });

      setData(data.data);
      setSuccess(true);
    } catch (e) {
      setValues({ ...values, captcha: "" });
      handleApiErr(e, {
        setErrors,
        t,
        toast,
        onError: (msg) => {
          if (msg === "err_cooldown") {
            authModal.open({
              message: t("login_register_to_continue_shorten"),
            });
            return true;
          }
          return false;
        },
      });
    }
  };

  const onCopy = () => {
    if (!canCopy) return;
    setCanCopy(false);
    copyText(shortUrl + "/" + data.short);
    toast.success("Copied to clipboard");
    setTimeout(() => {
      setCanCopy(true);
    }, 2000);
  };

  const downloadQrCode = async (short) => {
    qrRef.current?.download({
      name: "qr-" + data.short,
      extension: "png",
    });
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        long_url: Yup.string()
          .test("contain-sid", t("cannot_short_sid"), (value) => {
            return !sidLinkValidation.test(value || "");
          })
          .matches(linkValidation, t("invalid_url_format"))
          .required(t("validation_required", { field: t("long_url") })),
      }),
    [t]
  );

  return (
    <Formik
      initialValues={{ long_url: "", captcha: "" }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="flex lg:flex-row flex-col lg:items-center gap-3 bg-background p-3 border rounded-2xl">
            <div className="relative flex-1 order-2 lg:order-1">
              {success ? (
                <div className="lg:pl-3">
                  <h2 className="font-montserrat font-semibold text-foreground/60 text-sm lg:text-base">
                    <LinkIcon className="inline-block -mt-1 size-4" />{" "}
                    <Trans i18nKey="qr_created_successfully">
                      QR code created successfully.
                    </Trans>
                  </h2>

                  <button
                    type="button"
                    className="mt-2 font-montserrat font-semibold text-xl lg:text-3xl text-left hover:underline"
                    onClick={onCopy}
                  >
                    <span className="text-primary">s</span>.id/{data?.short}
                  </button>
                  <div className="mt-1 font-worksans text-foreground/50 truncate">
                    {data?.long_url}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      roundFull
                      className="flex flex-1 lg:flex-0 justify-center items-center mt-2 text-sm"
                      size="sm"
                      onClick={() => setSuccess(false)}
                    >
                      <ArrowLeftIcon className="mr-2 -ml-2 size-4" />
                      {t("back")}
                    </Button>
                    <Button
                      variant="secondary"
                      roundFull
                      className="flex flex-1 lg:flex-0 justify-center items-center mt-2 text-sm"
                      size="sm"
                      onClick={downloadQrCode}
                    >
                      <DownloadIcon className="mr-2 -ml-2 size-4" />
                      <Trans i18nKey="download_qr">Download QR</Trans>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Field
                      name="long_url"
                      type="text"
                      className={cn(
                        "bg-white",
                        "pr-[10rem] border focus:border-primary rounded-full outline-none ring-primary focus:ring-1 w-full font-montserrat font-medium transition-all",
                        "text-base lg:text-lg",
                        "py-3 px-5 lg:px-6 lg:py-4"
                      )}
                      placeholder={t("shorten_placeholder")}
                      disabled={isSubmitting}
                    />
                    <div className="top-0 right-1 lg:right-2 bottom-0 absolute flex items-center">
                      <Button
                        variant="primary"
                        roundFull
                        className={cn(
                          "flex items-center",
                          "!text-base lg:text-lg",
                          "!py-2 !px-5 !lg:px-6 !lg:py-4"
                        )}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <ZapIcon className="size-4" />
                        )}{" "}
                        <Trans i18nKey="create_qr">Create QR</Trans>
                      </Button>
                    </div>
                  </div>
                  <ErrorField
                    className="right-0 left-0 absolute bg-red-500 mt-1 px-6 py-1 rounded-full text-white"
                    name="long_url"
                  />
                </>
              )}
            </div>
            <div
              className={cn(
                "order-1 lg:order-2",
                "relative bg-white size-[10rem] overflow-hidden mx-auto",
                success ? "" : "border rounded-xl"
              )}
            >
              {success ? (
                <QRRender
                  url={shortUrl + "/" + data?.slug}
                  qrRef={(r) => (qrRef.current = r)}
                />
              ) : (
                <div className="absolute inset-0 flex justify-center items-center">
                  <QrCodeIcon className="size-8 text-foreground/50 scale-150" />
                </div>
              )}
            </div>
          </div>
          <p className="mt-1 text-foreground/50 text-sm text-center">
            <Trans i18nKey="form_agree_with_terms">
              Dengan memproses form ini anda telah setuju dengan semua{" "}
              <a
                href="/id/page/terms-of-service"
                target="_blank"
                className="text-red-500 underline"
              >
                ketentuan layanan
              </a>{" "}
              kami
            </Trans>
          </p>
          <InputHiddenCaptchaModal modalProp={captchaModal} action="short" />
        </Form>
      )}
    </Formik>
  );
}

function PublicMicrosite() {
  const api = useAPI();
  const { t } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [success, setSuccess] = useState(false);
  const captchaModal = useModal();
  const { authModal, status } = useUserContext();

  const onSubmit = async (values) => {
    if (values.captcha === "") {
      captchaModal.setOpen(true);
      return;
    }
    try {
      const data = await api.post("/claim-microsite", {
        slug: values.name,
        captcha: values.captcha,
      });
      //...
      setData({
        name: values.name,
        available: data?.data.available,
      });
      setSuccess(true);
    } catch (e) {
      if (e?.data.errors) {
        const msg = e?.data.errors.map((t) => t.reason).join(" ");
        if (/already taken/i.test(msg)) {
          setData({
            name: values.name,
            available: data?.data.available,
          });
          setSuccess(true);
        } else {
          toast.error(msg || t("connection_error"));
        }
      } else {
        toast.error(e?.data.message || t("connection_error"));
      }
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .matches(/^[a-zA-Z0-9\-]+$/, t("invalid_microsite_format"))
          .required(t("validation_required", { field: t("microsite") })),
      }),
    [t]
  );

  return (
    <div>
      <div className="relative">
        <Formik
          initialValues={{ name: "", captcha: "" }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting }) => (
            <Form
              className={cn(
                "transition-all duration-500",
                success
                  ? "pointer-events-none opacity-0 translate-y-3"
                  : "translate-y-0"
              )}
            >
              <div className="top-0 bottom-0 left-0 absolute flex items-center px-6 font-montserrat font-semibold text-lg">
                <span className="text-primary">s</span>.id/
              </div>
              <Field
                name="name"
                type="text"
                className={cn(
                  "bg-white",
                  "pr-[10rem] border focus:border-primary rounded-full outline-none ring-primary focus:ring-1 w-full font-montserrat font-medium transition-all",
                  "text-base lg:text-lg",
                  "py-3 px-5 lg:px-6 lg:py-4 !pl-[3.9rem]"
                )}
                placeholder={t("microsite_name_placeholder")}
                disabled={isSubmitting}
              />
              <ErrorField
                className="absolute bg-red-500 mt-1 px-6 py-1 rounded-full w-full text-white"
                name="name"
              />
              <div className="top-0 right-1 lg:right-2 bottom-0 absolute flex items-center">
                <Button
                  variant="primary"
                  roundFull
                  className="flex items-center"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <SearchIcon className="size-4" />
                  )}
                  <Trans i18nKey="check_availability">Check Availability</Trans>
                </Button>
              </div>
              <InputHiddenCaptchaModal
                modalProp={captchaModal}
                action="microsite_claim"
              />
            </Form>
          )}
        </Formik>
        <div
          className={cn(
            "transition-all",
            "duration-500",
            "top-0 right-0 left-0 absolute flex bg-white shadow-xl px-6 py-6 rounded-3xl",
            success
              ? "opacity-100 translate-y-0"
              : "opacity-0 pointer-events-none -translate-y-3"
          )}
        >
          <div className="flex-1">
            <h2 className="font-montserrat font-semibold text-foreground/60">
              {data?.available ? (
                <>
                  <CheckIcon className="inline-block -mt-1 size-4" />{" "}
                  <Trans i18nKey="microsite_available">
                    Microsite available
                  </Trans>
                </>
              ) : (
                <span className="text-red-500">
                  <XIcon className="inline-block -mt-1 size-4" />{" "}
                  <Trans i18nKey="microsite_not_available">
                    Microsite not available
                  </Trans>
                </span>
              )}
            </h2>

            <p className="mt-2 font-montserrat font-semibold text-3xl text-left">
              <span className="text-primary">s</span>.id/{data?.name}
            </p>
            {data?.available ? (
              <Button
                variant="secondary"
                roundFull
                className="mt-3"
                onClick={() => {
                  if (status === "authenticated") {
                    //@ts-ignore
                    window.location =
                      "/dashboard/microsite/create?utm_source=sid-landing&utm_campaign=claim-microsite&createWithName=" +
                      data.name;
                  } else {
                    authModal.open();
                  }
                }}
              >
                <Trans i18nKey="login_and_create_microsite">
                  Login and create microsite
                </Trans>
              </Button>
            ) : (
              <p className="mt-1 font-worksans text-foreground/50">
                <Trans i18nKey="try_another_name">
                  You can still use another name.
                </Trans>
              </p>
            )}
          </div>
          <div className="flex flex-col justify-between items-end">
            <button
              className="flex justify-center items-center border rounded-full size-10"
              onClick={() => setSuccess(false)}
            >
              <XIcon className="size-6" />
            </button>
          </div>
        </div>
      </div>
      <p className="mt-1 text-foreground/50 text-sm text-center">
        <Trans i18nKey="form_agree_with_terms">
          Dengan memproses form ini anda telah setuju dengan semua{" "}
          <a
            href="/id/page/terms-of-service"
            target="_blank"
            className="text-red-500 underline"
          >
            ketentuan layanan
          </a>{" "}
          kami
        </Trans>
      </p>
    </div>
  );
}
